<template>
  <div class="map-area bgw-pdg-20">
    <v-row>
      <v-col cols="8" md="8" class="map-aside">
        <div ref="map" style="width: 100%; height: 400px">
        </div>
        <div ref="popup" class="ol-popup" style="display: none">
          <a ref="popupCloser" class="ol-popup-closer" @click="closePopup"></a>
          <div contenteditable="true" ref="popupContent"></div>
        </div>
        <div class="map-panel">
          <p class="panel-tabs">
            <a @click="showMapPanelTab('layers')" :class="mapPanelTabClasses('layers')">{{ $t('Слои') }}</a>
          </p>
          <div class="panel-block layers-panel" v-show="mapPanel.tab === 'layers'">
            <div class="buttons has-addons">
              <button class="button is-light" v-for="(layer, key) in constBaseLayers"
                      :key="layer.id" :class="[{'is-info': layer.visible}, `layer-${layer.id}`]"
                      @click="showBaseLayer(key)">
                {{ layer.title }}
              </button>
            </div>
          </div>
        </div>
      </v-col>
      <v-col md="4" class="info-aside">
        <v-checkbox
          :key="mapLayer.id"
          v-for="mapLayer in ooptMapLayers"
          :label="mapLayer.name"
          v-model="mapLayer.visible"
          @change="showOoptLayer(mapLayer.id)"
        ></v-checkbox>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import MapAPI from '@/model/map'

// OpenLayers map components
import View from 'ol/View'
import Map from 'ol/Map'
import Overlay from 'ol/Overlay'
import { OSM, BingMaps, Vector as VectorSource } from 'ol/source'
import GeoJSON from 'ol/format/GeoJSON'
import { Group as LayerGroup, Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import Select from 'ol/interaction/Select'
import { Fill, Stroke, Style } from 'ol/style'

// importing the OpenLayers stylesheet is required for having
import 'ol/ol.css'

export default {
  props: {
    ooptId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      map: null,
      ooptLayers: null,
      baseLayers: null,
      zoom: 9.5,
      center: [74.78142227997782, 41.50321074902047],
      rotation: 0,
      overlay: null,
      constBaseLayers: [
        {
          id: 'osm',
          title: 'OpenStreetMap',
          visible: true,
          layer: new TileLayer({
            visible: true,
            source: new OSM()
          })
        },
        {
          id: 'bingmaps-aerial',
          title: 'Bing (аэрофотоснимки)',
          visible: false,
          layer: new TileLayer({
            visible: false,
            source: new BingMaps({
              key: 'AnsqOI94mG7dyEHeNhgOS2CQuDo82Fl0ELasOPPHXzIakF5oQFMSny9qYkDQbTel',
              imagerySet: 'AerialWithLabelsOnDemand'
            })
          })
        },
        {
          id: 'bingmaps-canvas',
          title: 'Bing (облегченная версия)',
          visible: false,
          layer: new TileLayer({
            visible: false,
            source: new BingMaps({
              key: 'AnsqOI94mG7dyEHeNhgOS2CQuDo82Fl0ELasOPPHXzIakF5oQFMSny9qYkDQbTel',
              imagerySet: 'CanvasLight'
            })
          })
        },
        {
          id: 'bingmaps-road',
          title: 'Bing (дороги)',
          visible: false,
          layer: new TileLayer({
            visible: false,
            source: new BingMaps({
              key: 'AnsqOI94mG7dyEHeNhgOS2CQuDo82Fl0ELasOPPHXzIakF5oQFMSny9qYkDQbTel',
              imagerySet: 'RoadOnDemand'
            })
          })
        }
      ],
      ooptMapLayers: [],
      mapPanel: {
        tab: 'layers'
      }
    }
  },
  created () {
    this.fetchData()
  },
  methods: {
    // getting layers data from backend
    fetchData () {
      MapAPI.getOoptLayers(this.ooptId)
        .then((response) => {
          this.ooptMapLayers = response.data.ooptMapLayers
          this.initiateMap()
        })
        .catch((error) => {
          console.log(error)
        })
    },
    // Initializing baseLayers
    initiateBaseLayers () {
      const baseLayer = []
      this.constBaseLayers.forEach(function (element) {
        baseLayer.push(element.layer)
      })
      this.baseLayers = new LayerGroup({
        layers: baseLayer
      })
    },
    // Initializing ooptLayers
    initiateOoptLayers () {
      // styles
      const style = new Style({
        stroke: new Stroke({
          color: '#7b993c',
          width: 1
        }),
        fill: new Fill({
          color: 'rgb(184, 223, 134, .5)'
        })
      })
      // adding vectors for layers
      const ooptLayer = []
      this.ooptMapLayers.forEach(function (element) {
        // a feature (geospatial object) is created from the GeoJSON
        const vectorSource = new VectorSource({
          features: new GeoJSON().readFeatures(element.geoJson)
        })
        const vectorLayer = new VectorLayer({
          id: element.id,
          source: vectorSource,
          style: style,
          visible: element.visible,
          layerName: element.name,
          layerDescription: element.description !== null ? element.description : ''
        })
        ooptLayer.push(vectorLayer)
      })
      // creating layer group
      this.ooptLayers = new LayerGroup({
        layers: ooptLayer
      })
    },
    // Initializing map
    initiateMap () {
      this.initiateBaseLayers()
      this.initiateOoptLayers()
      const popup = this.$refs.popup
      const popupContent = this.$refs.popupContent
      const transContent = [this.$i18n.t('Наименование'), this.$i18n.t('Описание')]
      popup.style.display = 'block'
      this.overlay = new Overlay({
        element: popup,
        autoPan: true,
        autoPanAnimation: {
          duration: 250
        }
      })
      const overlay = this.overlay
      // определение центра карты
      const ooptLayerArray = this.ooptLayers.getLayers().getArray()
      if (ooptLayerArray !== null) {
        const ooptLayerFeatures = ooptLayerArray[0].getSource().getFeatures()
        if (ooptLayerFeatures !== null) {
          const ooptLayerCoordinates = ooptLayerFeatures[0].getGeometry().getCoordinates()
          const lngLat = ooptLayerCoordinates[0][0][0]
          this.center = [lngLat[0], lngLat[1]]
        }
      }

      // this is where we create the OpenLayers map
      this.map = new Map({
        // the map will be created using the 'map' ref
        target: this.$refs.map,
        layers: [this.baseLayers, this.ooptLayers],
        // the map view will initially show the whole world
        view: new View({
          projection: 'EPSG:4326',
          zoom: this.zoom,
          center: this.center
        }),
        overlays: [this.overlay]
      })
      // selected styles
      const selectedStyle = new Style({
        stroke: new Stroke({
          color: '#7b993c',
          width: 2.5
        }),
        fill: new Fill({
          color: 'rgb(184, 223, 134, .5)'
        })
      })
      // adding selected style for map
      const select = new Select({
        style: selectedStyle
      })
      this.map.addInteraction(select)
      // popup overlay
      this.map.on('singleclick', function (evt) {
        const feature = this.forEachFeatureAtPixel(evt.pixel, function (feature, layer) {
          return {
            feature: feature,
            layer: layer
          }
        })
        if (feature) {
          const properties = feature.feature.getProperties()
          let layerName = feature.layer.get('layerName')
          if (properties.name !== undefined) {
            layerName += ': ' + properties.name
          }
          // set table block for selected feature
          const content = '<table>\n' +
            '<tr>\n' +
            '<th>' + transContent[0] + '</th>\n' +
            '<td>\n' + layerName + '</td>\n' +
            '</tr>\n' +
            '<tr>\n' +
            '<th>' + transContent[1] + '</th>\n' +
            '<td>' + feature.layer.get('layerDescription') + '</td>\n' +
            '</tr>\n' +
            '</table>'
          popupContent.innerHTML = content
          overlay.setPosition(evt.coordinate)
        }
      })
    },
    // show/hide oopt layers
    showOoptLayer (id) {
      this.ooptLayers.getLayers().forEach(function (element) {
        const properties = element.getProperties()
        if (properties.id === id) {
          element.setVisible(!element.getVisible())
        }
      })
    },
    // show/hide base layers
    showBaseLayer (id) {
      this.constBaseLayers.forEach(function (element, index) {
        if (index === id) {
          element.visible = true
        } else {
          element.visible = false
        }
      })

      this.baseLayers.getLayers().forEach(function (element, index) {
        if (index === id) {
          element.setVisible(true)
        } else {
          element.setVisible(false)
        }
      })
    },
    // map panel
    mapPanelTabClasses (tab) {
      return {
        'is-active': this.mapPanel.tab === tab
      }
    },
    showMapPanelTab (tab) {
      if (this.mapPanel.tab === tab) {
        this.mapPanel.tab = ''
      } else {
        this.mapPanel.tab = tab
      }
    },
    // closing popup
    closePopup () {
      this.overlay.setPosition(undefined)
      this.$refs.popupCloser.blur()
    }
  }
}
</script>
